import { useEffect, useMemo, useRef, useState } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../constants";
import {
  isTestsOf,
  levelsOf,
  packSizeOf,
  packUnitOf,
} from "../catalog/products/packagesEditor";
import { poNumber } from "./common";

const keyOf = (productId, variant) => `${productId}:${variant || ""}`;

/**
 * New purchase request: brand + variant + quantity (packaging units).
 * Only registered brands can be requested (Branded Products).
 * Every brand + variant is picked once from the search; picked ones are not offered again.
 * With an `order` it adds items to that P.O. instead, with their price; on a
 * received one they are received right away, so free items and lot / expiry too.
 */
export default function RequestModal({
  show,
  toggle,
  products,
  stock,
  onSubmit,
  isLoading,
  order,
  // brand + variant picked beforehand (Supply Hub cart): [{ productId, variant, qty }]
  initialLines = [],
}) {
  const isAdding = !!order,
    isReceived = order?.status === "received";
  const [lines, setLines] = useState([]),
    [search, setSearch] = useState(""),
    [isOpen, setIsOpen] = useState(false),
    [cursor, setCursor] = useState(0),
    [expected, setExpected] = useState(""),
    [remarks, setRemarks] = useState(""),
    searchRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setLines([]);
    setSearch("");
    setExpected("");
    setRemarks("");
  }, [show]);

  const onHandOf = (productId, variant) =>
    stock[keyOf(productId, variant)]?.onHand || 0;

  // every brand + variant of a known generic, e.g. SYR · Terumo · 3cc
  const options = useMemo(
    () =>
      products
        .filter(({ pgid }) => GenericProducts.find(pgid))
        .flatMap((product) => {
          const generic = GenericProducts.find(product.pgid);
          const variants = product.variants?.length
            ? product.variants
            : [{ _id: "", name: "" }];
          return variants.map((variant) => {
            const packQty = packSizeOf(variant, product);
            const levels = levelsOf(variant, product);
            return {
              key: keyOf(product._id, variant._id),
              productId: product._id,
              variant: variant._id || "",
              generic,
              brand: product.name,
              variantName: variant.name,
              unit: packUnitOf(variant, product),
              packQty,
              tracksLot: !!product.tracksLot,
              // reagents are counted in tests, so they are ordered by their kit only
              canByPiece: !isTestsOf(variant, product) && packQty > 1,
              // breakdown levels to order by (box, pack, case...), largest first
              levels: levels.length > 2 ? levels : [],
            };
          });
        })
        .sort(
          (a, b) =>
            a.generic.name.localeCompare(b.generic.name) ||
            a.brand.localeCompare(b.brand),
        ),
    [products],
  );

  const optionOf = (key) => options.find((option) => option.key === key);

  // not yet on the request, matching every word typed
  const matches = useMemo(() => {
    const picked = new Set(
      lines.map(({ productId, variant }) => keyOf(productId, variant)),
    );
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return options
      .filter(({ key }) => !picked.has(key))
      .filter(({ generic, brand, variantName }) => {
        const text = [generic.abbreviation, generic.name, brand, variantName]
          .join(" ")
          .toLowerCase();
        return words.every((word) => text.includes(word));
      });
  }, [options, lines, search]);

  useEffect(() => setCursor(0), [search]);

  // the cart of the Supply Hub, once the brands are known
  useEffect(() => {
    if (!show || !initialLines.length || !options.length) return;
    setLines(
      initialLines
        .map(({ productId, variant, qty }) => {
          const option = optionOf(keyOf(productId, variant));
          return option && { ...lineOf(option), qty: Number(qty) || 1 };
        })
        .filter(Boolean),
    );
    // only when opened, later changes are the user's
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, options.length]);

  const lineOf = (option) => ({
    productId: option.productId,
    variant: option.variant,
    qty: 1,
    // by the packaging unit (e.g. box) unless changed to pieces
    byPiece: false,
    // the packaging level ordered, the largest by default
    level: option.levels[0]?.bu || "",
    // adding to a P.O. only
    price: "",
    free: "",
    lot: "",
    expiry: "",
  });

  const pick = (option) => {
    if (!option) return;
    setLines((prev) => [
      ...prev,
      {
        productId: option.productId,
        variant: option.variant,
        qty: 1,
        // by the packaging unit (e.g. box) unless changed to pieces
        byPiece: false,
        // the packaging level ordered, the largest by default
        level: option.levels[0]?.bu || "",
        // adding to a P.O. only
        price: "",
        free: "",
        lot: "",
        expiry: "",
      },
    ]);
    setSearch("");
    setIsOpen(true);
    searchRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setCursor((prev) => Math.min(prev + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(matches[cursor]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const update = (index, changes) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...changes } : line)),
    );

  const valid = lines.filter(({ qty }) => Number(qty) > 0);

  // received right away: brands with lot tracking need their lot and expiry
  const lotMissing = isReceived
    ? valid.filter(
        ({ productId, variant, lot, expiry }) =>
          optionOf(keyOf(productId, variant))?.tracksLot &&
          (!lot.trim() || !expiry),
      )
    : [];

  const handleSubmit = () =>
    onSubmit({
      ...(isAdding ? { _id: order._id } : { expected, remarks }),
      items: valid.map((line) => ({
        productId: line.productId,
        variant: line.variant || undefined,
        qty: Number(line.qty),
        // a packaging level, else by pack / by piece
        level: line.level || undefined,
        byPiece: (!line.level && line.byPiece) || undefined,
        ...(isAdding && { price: Number(line.price) || undefined }),
        ...(isReceived && {
          freeItem: Number(line.free) || undefined,
          lot: line.lot.trim() || undefined,
          expiry: line.expiry || undefined,
        }),
      })),
    });

  const columns = 4 + (isAdding ? 1 : 0) + (isReceived ? 2 : 0);

  return (
    <MDBModal isOpen={show} toggle={toggle} backdrop size="xl">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="cart-plus" className="mr-2" />
        {isAdding ? `Add items to ${poNumber(order)}` : "New Purchase Request"}
      </MDBModalHeader>
      <MDBModalBody>
        {!products.length && (
          <div className="alert alert-warning py-1 px-2 small">
            No brand registered yet. Register brands in Products first.
          </div>
        )}

        {/* search to add */}
        <div className="position-relative mb-3">
          <MDBIcon
            icon="search"
            className="position-absolute text-muted"
            style={{ top: 11, left: 12 }}
          />
          <input
            ref={searchRef}
            className="form-control"
            style={{ paddingLeft: 36 }}
            placeholder="Search generic, brand or variant to add (e.g. syringe terumo 3cc)"
            value={search}
            disabled={!products.length}
            onChange={({ target }) => {
              setSearch(target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            // delayed so a click on an option is not lost
            onBlur={() => setTimeout(() => setIsOpen(false), 150)}
            onKeyDown={handleKeyDown}
          />
          {isOpen && (
            <div
              className="position-absolute w-100 bg-white border rounded z-depth-1"
              style={{ zIndex: 10, maxHeight: 260, overflowY: "auto" }}
            >
              {!matches.length && (
                <div className="px-3 py-2 small text-muted">
                  {options.length && lines.length === options.length
                    ? "Every registered brand is already on the request."
                    : "No matching brand."}
                </div>
              )}
              {matches.map((option, index) => (
                <div
                  key={option.key}
                  role="option"
                  aria-selected={index === cursor}
                  className={`px-3 py-1 d-flex align-items-center ${
                    index === cursor ? "blue lighten-5" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setCursor(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(option);
                  }}
                >
                  <code className="mr-2">{option.generic.abbreviation}</code>
                  <span className="text-truncate mr-2">
                    <b>{option.brand}</b>
                    {option.variantName && ` · ${option.variantName}`}
                    <small className="text-muted ml-2">
                      {option.generic.name}
                    </small>
                  </span>
                  <small className="ml-auto text-muted text-nowrap">
                    On hand: {onHandOf(option.productId, option.variant)}
                  </small>
                </div>
              ))}
            </div>
          )}
        </div>

        <MDBTable small hover className="mb-2">
          <thead className="grey lighten-4">
            <tr>
              <th>Item</th>
              <th style={{ width: 90 }} className="text-right">
                On hand
              </th>
              <th style={{ width: 260 }}>Quantity</th>
              {isReceived && (
                <th
                  style={{ width: 80 }}
                  title="Free items given by the supplier, e.g. 6+1"
                >
                  Free
                </th>
              )}
              {isAdding && (
                <th style={{ width: 120 }} title="Price per unit">
                  Price
                </th>
              )}
              {isReceived && <th style={{ width: 290 }}>Lot no. / expiry</th>}
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {!lines.length && (
              <tr>
                <td colSpan={columns} className="text-center text-muted py-3">
                  Search above to add the items{" "}
                  {isReceived ? "delivered" : "to purchase"}.
                </td>
              </tr>
            )}
            {lines.map((line, index) => {
              const option = optionOf(keyOf(line.productId, line.variant));
              if (!option) return null;
              return (
                <tr key={option.key}>
                  <td className="align-middle text-nowrap">
                    <code className="mr-2">{option.generic.abbreviation}</code>
                    <b>{option.brand}</b>
                    {option.variantName && ` · ${option.variantName}`}
                    <small className="text-muted ml-2">
                      {option.generic.name}
                    </small>
                  </td>
                  <td className="align-middle text-right">
                    {onHandOf(line.productId, line.variant)}
                  </td>
                  <td className="align-middle">
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={line.qty}
                        onChange={({ target }) =>
                          update(index, { qty: target.value })
                        }
                      />
                      <div className="input-group-append">
                        {option.levels.length ? (
                          // any packaging level: box, pack, case, pc...
                          <select
                            className="custom-select custom-select-sm"
                            style={{ borderRadius: "0 0.2rem 0.2rem 0" }}
                            value={line.level}
                            onChange={({ target }) =>
                              update(index, { level: target.value })
                            }
                          >
                            {option.levels.map(({ bu, qnty }, i) => (
                              <option key={bu} value={bu}>
                                {bu}
                                {i < option.levels.length - 1
                                  ? ` (${qnty.toLocaleString()} ${
                                      option.levels[option.levels.length - 1].bu
                                    })`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        ) : option.canByPiece ? (
                          <select
                            className="custom-select custom-select-sm"
                            style={{ borderRadius: "0 0.2rem 0.2rem 0" }}
                            value={line.byPiece ? "pcs" : "pack"}
                            onChange={({ target }) =>
                              update(index, {
                                byPiece: target.value === "pcs",
                              })
                            }
                          >
                            <option value="pack">
                              {option.unit || "pack"} ({option.packQty} pcs)
                            </option>
                            <option value="pcs">pcs</option>
                          </select>
                        ) : (
                          <span className="input-group-text">
                            {option.unit || "pcs"}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  {isReceived && (
                    <td className="align-middle">
                      <input
                        type="number"
                        min={0}
                        className="form-control form-control-sm"
                        value={line.free}
                        title="Free items (same unit), e.g. 1 for 6+1"
                        onChange={({ target }) =>
                          update(index, { free: target.value })
                        }
                      />
                    </td>
                  )}
                  {isAdding && (
                    <td className="align-middle">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="form-control form-control-sm"
                        placeholder="0.00"
                        value={line.price}
                        onChange={({ target }) =>
                          update(index, { price: target.value })
                        }
                      />
                    </td>
                  )}
                  {isReceived && (
                    <td className="align-middle">
                      <div className="d-flex" style={{ gap: 6 }}>
                        <input
                          className="form-control form-control-sm"
                          placeholder={
                            option.tracksLot ? "Lot no. (required)" : "Lot no."
                          }
                          value={line.lot}
                          onChange={({ target }) =>
                            update(index, { lot: target.value })
                          }
                        />
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          title="Expiry"
                          value={line.expiry}
                          onChange={({ target }) =>
                            update(index, { expiry: target.value })
                          }
                        />
                      </div>
                    </td>
                  )}
                  <td className="align-middle text-center">
                    <MDBIcon
                      icon="trash"
                      className="text-danger"
                      style={{ cursor: "pointer" }}
                      title="Remove"
                      onClick={() =>
                        setLines((prev) => prev.filter((_, i) => i !== index))
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>

        {lotMissing.length > 0 && (
          <div className="alert alert-danger py-1 px-2 small">
            Enter the lot number and expiry of{" "}
            <b>
              {lotMissing
                .map(
                  ({ productId, variant }) =>
                    optionOf(keyOf(productId, variant))?.brand,
                )
                .join(", ")}
            </b>
            .
          </div>
        )}
        {isReceived && (
          <small className="text-muted">
            This P.O. is already received: the items are received right away
            and added to the stock.
          </small>
        )}

        {!isAdding && (
          <div className="d-flex flex-wrap mt-3" style={{ gap: 12 }}>
            <div>
              <label className="small text-muted mb-1 d-block">
                Expected delivery
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={expected}
                onChange={({ target }) => setExpected(target.value)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label className="small text-muted mb-1 d-block">Remarks</label>
              <input
                className="form-control form-control-sm"
                value={remarks}
                placeholder="e.g. for the CBC drive next week"
                onChange={({ target }) => setRemarks(target.value)}
              />
            </div>
          </div>
        )}
      </MDBModalBody>
      <MDBModalFooter className="py-1">
        <small className="text-muted mr-auto">{valid.length} item(s)</small>
        <MDBBtn size="sm" color="light" onClick={toggle}>
          Cancel
        </MDBBtn>
        <MDBBtn
          size="sm"
          color="primary"
          onClick={handleSubmit}
          disabled={isLoading || !valid.length || lotMissing.length > 0}
        >
          <MDBIcon icon={isAdding ? "plus" : "paper-plane"} className="mr-1" />
          {isReceived
            ? "Add to P.O. & stock"
            : isAdding
              ? "Add to P.O."
              : "Submit request"}
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>
  );
}
